// i18n
import { translateInComponent, translate } from 'infras/i18n';
import translations from './i18n.json';

import styled from '@emotion/styled';
import Avatar from 'presentations/views/components/atoms/Avatar';
import Modal from 'presentations/views/components/atoms/Modal';
import {
  borderRadius,
  fontSize,
  fontWeight,
  mediaMax,
  spacing,
} from 'presentations/views/styles';
import { ViewerItem } from 'presentations/api/schema/user';
import {
  black,
  gray02,
  gray04,
  primary,
  white,
} from 'presentations/views/styles/color';
import { imagePath } from 'presentations/assets';
import Headline from 'presentations/views/components/atoms/Headline';
import Image from 'presentations/views/components/atoms/Image';
import Link from 'presentations/views/components/atoms/Link';
import DialogSubmitField from 'presentations/views/components/molecules/DialogSubmitField';
import { aiTermsPath, apiPath, helpPath } from 'presentations/routes/path';
import { useRequest } from 'presentations/views/hooks/request';
import { AssistantValidateItem } from 'integrations/assistant/presentations/api/schema/assistant_setting';
import { useEffect } from 'react';
import { setEvent } from 'presentations/views/helpers/analytics';

interface Props {
  viewer: ViewerItem;
  onClose: () => void;
  onSubmit: () => void;
}

export default function Component(props: Props) {
  const t = translateInComponent([translations]);
  const assistantValidateRequest = useRequest(
    apiPath('/v1/integrations/assistant/validate'),
  );
  const assistantValidate: AssistantValidateItem | null =
    assistantValidateRequest.data || null;

  useEffect(() => {
    setEvent('show chat assistant onboard');
  }, []);

  return (
    <StyledModal isOpen onChange={props.onClose}>
      <Container>
        <Title variant="h2">
          <TitleAvatar src={imagePath('assistant/icon.svg')} />
          Jicoo AI
          <BetaIcon>BETA</BetaIcon>
        </Title>
        <Description>
          {t(
            assistantValidate && assistantValidate.isValid
              ? 'Description'
              : 'Term Description',
          )}
        </Description>
        <OnboardMenuList>
          <OnboardMenuListItem target="_blank" href={helpPath('8439109')}>
            <MenuListItemImage src="assistant/icon.svg" alt={t('titles.0')} />
            <MenuListItemTitle>{t('titles.0')}</MenuListItemTitle>
          </OnboardMenuListItem>
          <OnboardMenuListItem
            target="_blank"
            href={helpPath('8439109#h_f3e4d8fcd0')}
          >
            <MenuListItemImage
              src="assistant/schedule.svg"
              alt={t('titles.1')}
            />
            <MenuListItemTitle>{t('titles.1')}</MenuListItemTitle>
          </OnboardMenuListItem>
          <OnboardMenuListItem
            target="_blank"
            href={helpPath('8439109#h_35998ad2f1')}
          >
            <MenuListItemImage
              src="assistant/optimize.svg"
              alt={t('titles.2')}
            />
            <MenuListItemTitle>{t('titles.2')}</MenuListItemTitle>
          </OnboardMenuListItem>
          <OnboardMenuListItem
            target="_blank"
            href={helpPath('8439109#h_f142892430')}
          >
            <MenuListItemImage
              src="assistant/summary.svg"
              alt={t('titles.3')}
            />
            <MenuListItemTitle>{t('titles.3')}</MenuListItemTitle>
          </OnboardMenuListItem>
        </OnboardMenuList>
        <TermContainer>
          <TermText>
            {t('terms.0')}
            <Link href={aiTermsPath()} target="_blank">
              {t('terms.1')}
            </Link>
            {t('terms.2')}
          </TermText>
          <DialogSubmitField
            onSubmit={props.onSubmit}
            onCancel={props.onClose}
            submitLabel={t('Activate')}
          />
        </TermContainer>
      </Container>
    </StyledModal>
  );
}

const StyledModal = styled(Modal)``;
const Container = styled.div`
  position: relative;
  padding: ${spacing(24)};
  background-color: ${white};
  border-radius: ${borderRadius(16)};
  border: 1px solid ${gray04};
  ${mediaMax.tablet} {
  }
`;
const Title = styled(Headline)`
  margin-bottom: ${spacing(8)};
  display: flex;
  align-items: center;
`;
const Description = styled.div`
  margin-bottom: ${spacing(16)};
`;
const OnboardMenuList = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  grid-gap: ${spacing(16)};
  ${mediaMax.tablet} {
  }
`;
const OnboardMenuListItem = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid ${gray04};
  border-radius: ${borderRadius(6)};
  padding: ${spacing(16)};
  color: ${black};
  :hover {
    color: ${primary};
    border-color: ${primary};
  }
`;
const MenuListItemImage = styled(Image)`
  margin-bottom: ${spacing(16)};
`;
const MenuListItemTitle = styled.div`
  font-weight: ${fontWeight('bold')};
  font-size: ${fontSize(15)};
`;

const TermContainer = styled.div`
  margin-top: ${spacing(32)};
  text-align: center;
`;
const TermText = styled.div`
  margin-bottom: ${spacing(16)};
`;
const BetaIcon = styled.div`
  font-weight: ${fontWeight('normal')};
  margin-left: ${spacing(8)};
  font-size: ${fontSize(11)};
  line-height: ${fontSize(11)};
  border: 1px solid ${gray02};
  border-radius: ${borderRadius(4)};
  padding: ${spacing(6)};
  color: ${gray02};
  height: fit-content;
`;

const TitleAvatar = styled(Avatar)`
  width: 26px;
  height: 26px;
  margin-right: ${spacing(8)};
`;
